// FIXME: Automatically disabled during ESLint config update
// eslint-disable-next-line n/no-extraneous-import
import { Switch as BaseSwitch, type SwitchProps as BaseSwitchProps } from "@mui/base/Switch";
import { type Theme } from "@mui/material";
import { styled } from "@mui/material/styles";

const Root = styled("span")<{ theme?: Theme; ownerState: { disabled?: boolean } }>(
  ({ ownerState }) => ({
    display: "inline-block",
    width: "3.0625rem", // 49px
    height: "2rem", // 32px
    padding: 0,
    position: "relative",
    cursor: ownerState.disabled ? "not-allowed" : "pointer",
  })
);

const Track = styled("span")<{
  theme?: Theme;
  ownerState: { checked?: boolean; disabled?: boolean };
}>(({ theme, ownerState }) => ({
  backgroundColor: ownerState.checked ? theme.palette.primary.main : theme.palette.border?.primary,
  borderRadius: "1.25rem", // 20px
  display: "block",
  height: "100%",
  width: "100%",
  position: "absolute",
  opacity: ownerState.disabled ? 0.4 : 1,
  transition: "background-color 150ms cubic-bezier(0.4, 0, 0.2, 1)",
}));

const Thumb = styled("span")<{ theme?: Theme; ownerState: { checked?: boolean } }>(
  ({ theme, ownerState }) => ({
    display: "block",
    width: "1.75rem", // 28px
    height: "1.75rem", // 28px
    backgroundColor: theme.palette.background.tertiary,
    borderRadius: "50%",
    boxShadow: theme.shadows[1],
    position: "absolute",
    top: "0.125rem", // 2px
    left: "0.125rem", // 2px
    transition: "transform 150ms cubic-bezier(0.4, 0, 0.2, 1)",
    transform: ownerState.checked ? "translateX(1.0625rem)" : "translateX(0)",
  })
);

const Input = styled("input")({
  cursor: "inherit",
  position: "absolute",
  width: "100%",
  height: "100%",
  top: 0,
  left: 0,
  opacity: 0,
  zIndex: 1,
  margin: 0,
});

export interface SwitchProps extends Omit<BaseSwitchProps, "slots" | "slotProps"> {
  /**
   * If true, the switch will be disabled.
   * @default false
   */
  disabled?: boolean;
}

/**
 * A switch component that is styled to match the Clipboard Health design system.
 * This should be added to our core component library, but there are conflicts trying to install @mui/base.
 * @param props - The props for the switch component.
 * @returns The switch component.
 */

export function Switch({ disabled = false, ...props }: SwitchProps) {
  return (
    <BaseSwitch
      slots={{
        root: Root,
        track: Track,
        thumb: Thumb,
        input: Input,
      }}
      disabled={disabled}
      {...props}
    />
  );
}

Switch.displayName = "Switch";
